import type { VehicleDetailsEntity } from '../entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { toVehicleDetailsEntity } from '../helpers/mappers';

/** Parameters for fetching vehicle details. */
export type GetVehicleDetailsRequest = {
    /** The VIN (Vehicle Identification Number) to fetch details for. Accepts 8 or 17 characters. */
    vin: string;
};

/** Response for fetching vehicle details. */
export type GetVehicleDetailsResponse = {
    /** The list of vehicle details matching the request criteria. */
    results: VehicleDetailsEntity[];
};

/**
 * Fetch vehicle details for a given VIN. Returns a list of vehicle details matching the VIN,
 * as there can be multiple records for a single VIN in the case of 8-character VINs which are not unique.
 */
export class FetchVehicleDetails extends StsBackendOperation {
    /**
     * Fetch vehicle details for a given VIN.
     *
     * @param params - the VIN to fetch details for
     * @param signal - optional abort signal for request cancellation
     * @returns the list of vehicle details matching the request criteria
     */
    async exec({ vin }: GetVehicleDetailsRequest, signal?: AbortSignal): Promise<VehicleDetailsEntity[] | undefined> {
        const response = await this.client.fetchVehicleDetails({ vin }, signal);

        if (!response.success || !response.data) {
            return undefined;
        }

        const results = response.data.results;

        if (!results?.length) {
            return [];
        }

        return results.map(toVehicleDetailsEntity);
    }
}
